import type { LucideIcon } from "lucide-react";
import { Hash, LayoutGrid, Music, Palette } from "lucide-react";
import type { Trend, TrendPillarFit, TrendPlatform, TrendStats, TrendType } from "@/types/trend.type";

/** A kind is an icon and a word, in neutral ink, and never a colour. Pillar hues
 *  mean a strategy pillar, and a card that also says which pillar a trend fits
 *  cannot use one hue for two things, so no kind borrows one. */
export const TYPE_META: Record<TrendType, { label: string; icon: LucideIcon }> = {
  format: { label: "Format", icon: LayoutGrid },
  topic: { label: "Topic", icon: Hash },
  sound: { label: "Sound", icon: Music },
  style: { label: "Style", icon: Palette },
};

export const TYPE_LABEL: Record<TrendType, string> = {
  format: TYPE_META.format.label,
  topic: TYPE_META.topic.label,
  sound: TYPE_META.sound.label,
  style: TYPE_META.style.label,
};

export const TYPE_OPTIONS: TrendType[] = ["format", "topic", "sound", "style"];

export const PLATFORM_LABEL: Record<string, string> = {
  tiktok: "TikTok",
  instagram: "Instagram",
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABEL[platform] ?? platform;
}

/**
 * The platforms a trend was found on, as every surface prints them: the card's
 * marks, the phone feed's marks and the sheet's WHERE IT WAS FOUND. One rule, so
 * the card and the sheet can never name different platforms for one trend.
 *
 * The trend's own list first. Where the run recorded none, the distinct
 * platforms of the examples in hand: the detail's example rows, and the feed's
 * preview clips, which are examples too. Where there is neither, null, and the
 * caller says the platform was not recorded rather than guessing one.
 */
export function trendPlatforms(
  trend: Pick<Trend, "platforms" | "previewVideo" | "previewVideos"> & {
    examples?: readonly { platform: TrendPlatform }[];
  },
): TrendPlatform[] | null {
  const own = Array.isArray(trend.platforms) ? trend.platforms.filter(Boolean) : [];
  if (own.length > 0) return own;
  const seen: TrendPlatform[] = [];
  const clips = [...(trend.examples ?? []), ...(trend.previewVideos ?? []), trend.previewVideo];
  for (const clip of clips) {
    if (clip?.platform && !seen.includes(clip.platform)) seen.push(clip.platform);
  }
  return seen.length > 0 ? seen : null;
}

/** The two values the discovery run plans against get a display form. The column
 *  itself is free text written by the distiller, so anything else is printed
 *  verbatim: the library is the authority on what its own tag says, and a guess
 *  here would be a fabricated fact. */
const REGION_LABEL: Record<string, string> = { MENA: "MENA", global: "Global" };
const LANGUAGE_LABEL: Record<string, string> = { ar: "Arabic", en: "English" };

export function regionLabel(region: string | null): string {
  if (!region) return "region not set";
  return REGION_LABEL[region] ?? region;
}

export function languageLabel(language: string | null): string {
  if (!language) return "language not set";
  return LANGUAGE_LABEL[language] ?? language;
}

/** A stored tag that says nothing: absent, blank, or literally "unknown". */
function isUnset(value: string | null): boolean {
  return value === null || value.trim() === "" || value.trim().toLowerCase() === "unknown";
}

/** Where the example videos were found, as a card prints it: region, then
 *  language, and only the halves that carry a real value. A missing field is
 *  omitted, never printed, so "region not set" can never reach a card; with
 *  both missing there is no line at all and this answers null. The detail
 *  panel still prints both cells through regionLabel and languageLabel, where
 *  a labelled empty cell is the honest answer. */
export function originLabel(trend: Pick<Trend, "region" | "language">): string | null {
  const parts = [
    isUnset(trend.region) ? null : regionLabel(trend.region),
    isUnset(trend.language) ? null : languageLabel(trend.language),
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : null;
}

const NO_STATS: TrendStats = { exampleCount: 0, totalViews: 0, totalLikes: 0 };

/**
 * The stats block, read defensively.
 *
 * Nothing validates the API response against the TrendStats type at runtime in
 * this app, so a missing or malformed block would throw on the first destructure
 * and take the whole wall down. It reads as zeros instead, which every surface
 * already treats as NOT MEASURED rather than as a measured zero.
 */
export function trendStats(trend: Pick<Trend, "stats">): TrendStats {
  const raw = trend.stats as Partial<TrendStats> | null | undefined;
  if (!raw) return NO_STATS;
  const count = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0);
  return {
    exampleCount: count(raw.exampleCount),
    totalViews: count(raw.totalViews),
    totalLikes: count(raw.totalLikes),
  };
}

export function compactTrendNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

export function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 30 * 86_400) return `${Math.floor(seconds / 86_400)}d ago`;
  const months = Math.floor(seconds / (30 * 86_400));
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

/** A count said out loud: "574.1 thousand" rather than "574.1k", which a screen
 *  reader would otherwise spell out letter by letter. */
export function spokenTrendNum(n: number): string {
  return compactTrendNum(n).replace(/k$/, " thousand").replace(/M$/, " million");
}

/** The FOUND figure on a card: timeAgo without its "ago", in caps ("5D",
 *  "2MO"). The same helper, so the card and the panel can never disagree. */
export function compactAge(iso: string): string {
  return timeAgo(iso).replace(/\s*ago$/, "").toUpperCase();
}

const AGE_WORDS: Record<string, readonly [string, string]> = {
  m: ["minute", "minutes"],
  h: ["hour", "hours"],
  d: ["day", "days"],
  mo: ["month", "months"],
  y: ["year", "years"],
};

/** The same age in words, for a sentence read aloud: "5 days ago". */
export function spokenAge(iso: string): string {
  const short = timeAgo(iso);
  const match = /^(\d+)(mo|m|h|d|y) ago$/.exec(short);
  if (!match) return short;
  const n = Number(match[1]);
  const [one, many] = AGE_WORDS[match[2]];
  return `${n} ${n === 1 ? one : many} ago`;
}

/**
 * The two counts a card or the phone feed prints, or null where nothing was
 * counted. A total of zero is read as NOT COUNTED, the way every surface reads
 * trendStats, never as a measured zero; both are floors, printed with a "+".
 */
export function countedFigures(trend: Pick<Trend, "stats">): { views: number | null; likes: number | null } {
  const { exampleCount, totalViews, totalLikes } = trendStats(trend);
  return {
    views: exampleCount > 0 && totalViews > 0 ? totalViews : null,
    likes: exampleCount > 0 && totalLikes > 0 ? totalLikes : null,
  };
}

/** The figures row said as one sentence, for a screen reader: the visible row
 *  is three bare numbers whose meaning is carried by their position. */
export function figuresSentence(views: number | null, likes: number | null, createdAt: string): string {
  const found = `Found ${spokenAge(createdAt)}.`;
  if (views !== null && likes !== null) {
    return `At least ${spokenTrendNum(views)} views and ${spokenTrendNum(likes)} likes on its examples. ${found}`;
  }
  if (views !== null) {
    return `At least ${spokenTrendNum(views)} views on its examples. Likes not counted. ${found}`;
  }
  if (likes !== null) {
    return `At least ${spokenTrendNum(likes)} likes on its examples. Views not counted. ${found}`;
  }
  return `Views and likes not counted. ${found}`;
}

/**
 * The one pillar a card names: the closest fit, or null when the trend carries
 * no fits. Read defensively, because the field is not sent yet and nothing
 * validates a row at runtime (backend request:
 * https://github.com/Mo7tawa/creative-os-backend/issues/91).
 */
export function topPillarFit(trend: Pick<Trend, "pillarFit">): TrendPillarFit | null {
  const fits = Array.isArray(trend.pillarFit)
    ? trend.pillarFit.filter(
        (fit) =>
          fit != null &&
          typeof fit.pillarId === "string" &&
          typeof fit.pillarName === "string" &&
          fit.pillarName.trim() !== "" &&
          Number.isFinite(fit.similarity),
      )
    : [];
  if (fits.length === 0) return null;
  return fits.reduce((best, fit) => (fit.similarity > best.similarity ? fit : best));
}

/**
 * The "since" in "4 new since Tuesday": when the viewer last looked, in words.
 *
 * The weekday, as the design asks, but only inside the last six days, where a
 * weekday can only mean one day: further back, "since Tuesday" would point at
 * the wrong week, so it becomes a short date ("3 Sep"). A visit earlier the
 * same day is "earlier today", because the weekday would be today's own name.
 */
export function sinceLabel(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (then.getTime() >= startOfToday) return "earlier today";
  const daysBack = (startOfToday - then.getTime()) / 86_400_000;
  if (daysBack < 6) return then.toLocaleDateString("en-US", { weekday: "long" });
  return then.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * The sentence the whole page is built to keep honest, and it has to carry three
 * true things at once.
 *
 *  1. The numbers are the CREATORS'. They belong to the videos discovery
 *     harvested, never to the reader's brand.
 *  2. They are a SNAPSHOT, from the discovery run that last saw this trend.
 *     Nothing measures a trend continuously, but a later run that re-identifies
 *     the same trend appends a fresh observation and the feed reads the newest
 *     one, so the figure can change.
 *  3. They are a FLOOR. The harvest aggregate adds nothing for an example whose
 *     count never came back, so a total can only ever understate.
 */
export function evidenceSentence(trend: Pick<Trend, "stats">): string {
  const { exampleCount, totalViews, totalLikes } = trendStats(trend);
  if (exampleCount === 0) {
    return "No example videos came back with this trend, so nothing about it has been counted yet.";
  }
  const videos = exampleCount === 1 ? "source video holds" : "source videos hold";
  return `Those ${exampleCount} ${videos} at least ${compactTrendNum(
    totalViews,
  )} views and ${compactTrendNum(
    totalLikes,
  )} likes between them. Creator numbers, not yours, from the discovery run that last saw this trend.`;
}

/**
 * What a panel holding the example rows can and cannot vouch for about the two
 * totals it is drawing.
 *
 * The detail route lists at most six examples while the totals are aggregated
 * over every example on the observation, and an example whose count never came
 * back contributes zero to the aggregate. So either of those makes the totals a
 * floor, and the panel has to say which one it is looking at rather than letting
 * two 28px numerals read as an exact sum.
 */
export function evidenceCoverage(
  exampleCount: number,
  examples: readonly { viewCount: number | null; likeCount: number | null }[],
): string | null {
  if (exampleCount === 0) return null;
  const uncounted = examples.filter((e) => e.viewCount == null || e.likeCount == null).length;
  const unlisted = Math.max(0, exampleCount - examples.length);
  /* The rule always holds. "The real numbers are higher" only holds where an
     uncounted video has actually been SEEN: where the panel merely cannot check,
     saying the figures are understated would be its own overclaim. */
  const rule =
    "A video that came back without its count adds nothing to a total, so a figure here is a floor and never an exact sum.";
  const higher = "Both figures cover only the counted videos, so the real numbers are higher.";

  if (uncounted > 0 && unlisted > 0) {
    return `${uncounted} of the examples in the carousel came back without a count, and ${unlisted} more are not in it at all. ${rule} ${higher}`;
  }
  if (uncounted > 0) {
    return `${uncounted} of these videos came back without a count. ${rule} ${higher}`;
  }
  if (unlisted > 0) {
    return `${unlisted} of these videos are not in the carousel, so this panel cannot check whether every one of them came back with a count. ${rule}`;
  }
  return null;
}

/**
 * What the Recreate action carries to the Workshop. Handed to the draft chat
 * through navigation state (WorkshopView reads it once at mount); nothing about
 * it is written anywhere.
 *
 * It lives HERE, in the pure meta module, rather than beside the button that
 * first used it, because two surfaces now run Recreate — the trend panel and
 * the Home rail's "Trend of the week" — and a second copy of this brief would
 * be the one that silently goes stale. Keeping it out of the modal's own module
 * also keeps a React component tree out of Home's data layer.
 *
 * Typed to `Trend` rather than `TrendDetail`: it reads only the fields every
 * feed row already carries, and `TrendDetail` extends `Trend`, so both callers
 * type-check against the narrower shape.
 */
export function recreatePrompt(trend: Trend): string {
  const descriptor = [trend.type, trend.platforms.join(", ")].filter(Boolean).join("; ");
  return [
    `Recreate this trend for our brand: "${trend.title}" (${descriptor}).`,
    `Trend id: ${trend.id}. Pull the full details and examples with GetTrendDetail.`,
    "Study what makes the format work, then develop ONE concrete concept idea adapted",
    "to our brand and save it with SaveConceptIdea, citing the trend as a source.",
  ].join("\n");
}
